/*
 * capabilities.ts - the client capabilities advertised to the tileserver
 */

import * as viewerConfig from '../viewer-config';


/**
 * The document types a capability set is advertised for. Each type gets
 * its own set: a vector layer negotiates heightcoding and tile format,
 * a terrain source will negotiate texture formats, a raster source its
 * own. Only `vector` exists today.
 */
export type DocumentType = 'vector';


/**
 * Returns the query string that advertises this client's capabilities on
 * a layer document request of the given type, or an empty string when
 * there is nothing to advertise. The tileserver answers with tile URLs
 * tailored to the set.
 *
 * The names are sorted so the string is the same for every client with
 * the same capabilities: it is part of the CDN cache key.
 *
 * @param config viewer configuration
 * @param type the document the capabilities are advertised for
 * @returns `?caps=<name>,<name>...` or `''`
 */
export function capsQuery(
    config: viewerConfig.ViewerConfig,
    type: DocumentType,
): string {

    const caps: string[] = [];

    if (type === 'vector') {

        // heightcoding.client: the elevation store heightcodes delivered
        // vector data, so the tileserver serves planar tiles
        if (config.mapHeightcoding === 'store')
            caps.push('heightcoding.client');

        // format.mvt: Mapbox vector tiles instead of planar geodata; they
        // are planar, so they need heightcoding.client, and binary, so
        // they need the worker fetch
        if (config.mapHeightcoding === 'store' && config.mapVectorTilesMvt
                && config.mapGeodataFetchInWorker)
            caps.push('format.mvt');
    }

    if (caps.length === 0) return '';
    return '?caps=' + caps.sort().join(',');
}
